import useMessageAndSeverity from "#webapp/src/components/hooks/useMessageAndSeverity";
import { getCurrentOrg } from "#webapp/src/util/org.util";
import { useMutation, useQuery } from "@apollo/client";
import { logger } from "@clockwise/client-commons/src/util/logger";
import { Switch } from "@clockwise/design-system";
import { RecurrenceType, WebappSurface } from "@clockwise/schema";
import EventCard from "@clockwise/web-commons/src/components/EventCard";
import EventCardColumns from "@clockwise/web-commons/src/components/EventCardColumns";
import EventCardColumn from "@clockwise/web-commons/src/components/EventCardColumns/EventCardColumn";
import { TrackingEvents, useTracking } from "@clockwise/web-commons/src/util/analytics.util";
import { Tooltip } from "@material-ui/core";
import { every, filter, isEqual, map } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { throttle } from "throttle-debounce";
import {
  SetCalendarEventFlexibilityDocument,
  SetCalendarEventFlexibilityMutation,
} from "../../graphql/__generated__/SetCalendarEventFlexibility.generated";
import { useMonetization } from "../../hooks/useMonetization";
import { ScrollIntoView } from "../ui-focus-wrappers/ScrollIntoView";
import OneOnOneGuidance from "./OneOnOneGuidance";
import { OneOnOnesQueryDocument } from "./graphql/__generated__/OneOnOnes.generated";
import { OneOnOne } from "./types";
import parseOneOnOneStatsGQL from "./utils/parseOneOnOneStatsGQL";
import parseOneOnOnesGQL from "./utils/parseOneOnOnesGQL";

const mapEventToEventCard = (oneOnOne: OneOnOne, canUserAccessFlexMeetings = true) => (
  <EventCard
    conflicted={oneOnOne.conflicted}
    editable={oneOnOne.editable}
    eventId={oneOnOne.id}
    flexible={oneOnOne.flexible && canUserAccessFlexMeetings}
    key={oneOnOne.id}
    recurrence={oneOnOne.recurrenceText}
    title={oneOnOne.title}
    restricted={!canUserAccessFlexMeetings}
  />
);

const trackingSourceId = "One-on-ones control";

const OneOnOnes = () => {
  const [oneOnOnes, setOneOnOnes] = useState<OneOnOne[]>([]);
  const { setInfo, setError } = useMessageAndSeverity();
  const trackEvent = useTracking();
  const { error, loading, data } = useQuery(OneOnOnesQueryDocument);
  const [setFlexibility] = useMutation(SetCalendarEventFlexibilityDocument);

  const throttledSetFlexiblility = useRef(throttle(250, setFlexibility)).current;
  const { canUserAccessFlexMeetings } = useMonetization();

  useEffect(() => {
    if (data) {
      setOneOnOnes(parseOneOnOnesGQL(data));
    }
  }, [data]);

  const { oneOnOneAvgWeekly } = parseOneOnOneStatsGQL(data);
  const org = getCurrentOrg(data?.viewer);
  const orgId = org?.id || "";

  const weeklyOneOnOnes = filter<OneOnOne>(oneOnOnes, { recurrance: RecurrenceType.Weekly });
  const biweeklyOneOnOnes = filter<OneOnOne>(oneOnOnes, { recurrance: RecurrenceType.Fortnightly });
  const infrequentOneOnOnes = filter<OneOnOne>(
    oneOnOnes,
    ({ recurrance }) =>
      recurrance !== RecurrenceType.Weekly && recurrance !== RecurrenceType.Fortnightly,
  );

  const updateOneFlexibility = (id: string, flexible: boolean) => {
    setOneOnOnes((state) => {
      return state.map((oneOnOne) => {
        if (oneOnOne.id === id) {
          return Object.assign({}, oneOnOne, { flexible });
        }

        return oneOnOne;
      });
    });
  };

  const updateAllFlexibility = (flexible: boolean) => {
    setOneOnOnes((state) => {
      return state.map((oneOnOne) => {
        return Object.assign({}, oneOnOne, { flexible });
      });
    });
  };

  const handleSetAllFlexibilitySuccess = (allFlexible: boolean) => {
    setInfo("Successfully updated all one-on-ones flexibility");
    trackEvent(TrackingEvents.FLEXIBLE_MEETING.UPDATE_FLEXIBILITY_MASS, {
      allFlexible,
      source: trackingSourceId,
    });
  };

  const handleSetAllFlexibilityErrored = () => {
    setError("Unable to update all one-on-ones flexibility");
    logger.error("Failed to update all one-on-ones flexibility");
  };

  const handleAllFlexibilityChange = (allFlexible: boolean) => {
    // error if missing required prop
    if (!orgId) {
      handleSetAllFlexibilityErrored();
      return;
    }

    const eventsToUpdate = map(oneOnOnes, "id");

    const optimisticResponse: SetCalendarEventFlexibilityMutation = {
      __typename: "Mutation",
      setCalendarEventFlexibility: {
        __typename: "SetCalendarEventFlexibilityPayload",
        updatedEvents: map(eventsToUpdate, (eventId) => ({
          __typename: "CalendarEvent",
          id: eventId,
          flexibility: {
            __typename: allFlexible ? "EventFlexibility" : "NotFlexible",
          },
        })),
      },
    };

    void throttledSetFlexiblility({
      variables: {
        input: {
          orgId,
          eventsToEnable: allFlexible ? eventsToUpdate : [],
          eventsToDisable: allFlexible ? [] : eventsToUpdate,
        },
      },
      optimisticResponse,
      onCompleted: (data) => {
        let hasErrors = false;
        optimisticResponse.setCalendarEventFlexibility?.updatedEvents?.forEach(
          (optimisticEvent) => {
            const actualEvent = data.setCalendarEventFlexibility?.updatedEvents?.find((event) => {
              return optimisticEvent.id === event.id;
            });

            if (!isEqual(optimisticEvent, actualEvent)) {
              hasErrors = true;
            }
          },
        );

        if (hasErrors) {
          handleSetAllFlexibilityErrored();
        } else {
          handleSetAllFlexibilitySuccess(allFlexible);
          updateAllFlexibility(allFlexible);
        }
      },
      onError: handleSetAllFlexibilityErrored,
    });
  };

  const handleSetOneFlexibilitySuccess = (eventId: string, isFlexible: boolean) => {
    setInfo("Successfully updated one-on-one flexibility");
    trackEvent(TrackingEvents.FLEXIBLE_MEETING.UPDATE_FLEXIBILITY, {
      isFlexible,
      eventId,
      source: trackingSourceId,
    });
  };

  const handleSetOneFlexibilityErrored = () => {
    setError("Unable to update flexibility");
    logger.error("Failed to update one-on-one flexibility");
  };

  const handleOneFlexibilityChange = (eventId: string, isFlexible: boolean) => {
    const eventsToEnable = [];
    const eventsToDisable = [];

    // error if missing required prop
    if (!orgId) {
      handleSetOneFlexibilityErrored();
      return;
    }

    if (isFlexible) {
      eventsToEnable.push(eventId);
    } else {
      eventsToDisable.push(eventId);
    }

    // optimistically update UI
    updateOneFlexibility(eventId, isFlexible);

    // void setFlexibility({
    void throttledSetFlexiblility({
      variables: { input: { orgId, eventsToEnable, eventsToDisable } },
      onCompleted: () => handleSetOneFlexibilitySuccess(eventId, isFlexible),
      onError: () => {
        handleSetOneFlexibilityErrored();
        updateOneFlexibility(eventId, !isFlexible);
      },
    });
  };

  const allFlexible = every(oneOnOnes, "flexible");
  const fetchError = error?.message ? "Unable to fetch One-on-ones" : undefined;

  const switchTooltipText = canUserAccessFlexMeetings ? "" : "Upgrade to mark meetings as flexible";

  return (
    <>
      <ScrollIntoView focusSection={WebappSurface.FlexibleMeetings}>
        <div className="cw-mb-3 cw-border-0 cw-border-b-[1px] cw-border-default cw-border-solid">
          <div className="cw-flex cw-flex-wrap cw-items-baseline cw-mb-1">
            <h2 className="cw-heading-2xl cw-grow cw-mb-0">One-on-ones</h2>
            <div className="cw-relative cw-top-[5px] cw-right-[2px]">
              <Tooltip title={switchTooltipText}>
                <div className="cw-pb-[2px]">
                  <Switch
                    label={<div className="cw-body-sm">Mark all one-on-ones flexible</div>}
                    labelPlacement="start"
                    checked={allFlexible}
                    disabled={loading || !oneOnOnes.length || !canUserAccessFlexMeetings}
                    onChange={handleAllFlexibilityChange}
                    cw-id="mark-all-flexible-switch"
                  />
                </div>
              </Tooltip>
            </div>
          </div>
        </div>
        <OneOnOneGuidance loading={loading} weeklyAverage={oneOnOneAvgWeekly} />
        <EventCardColumns
          headingLevel="h3"
          loading={loading}
          errorMessage={fetchError}
          onFlexibilityChange={handleOneFlexibilityChange}
        >
          <EventCardColumn heading="Weekly" emptyMessage="No weekly one-on-ones">
            {weeklyOneOnOnes.map((oneOnOne) =>
              mapEventToEventCard(oneOnOne, canUserAccessFlexMeetings),
            )}
          </EventCardColumn>
          <EventCardColumn
            heading="Every 2 weeks"
            emptyMessage="No one-on-ones that repeat every 2 weeks"
          >
            {biweeklyOneOnOnes.map((oneOnOne) =>
              mapEventToEventCard(oneOnOne, canUserAccessFlexMeetings),
            )}
          </EventCardColumn>
          <EventCardColumn heading="Less frequent" emptyMessage="No less frequent one-on-ones">
            {infrequentOneOnOnes.map((oneOnOne) =>
              mapEventToEventCard(oneOnOne, canUserAccessFlexMeetings),
            )}
          </EventCardColumn>
        </EventCardColumns>
      </ScrollIntoView>
    </>
  );
};

export default OneOnOnes;
